import React, { FC, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useFormContext, useWatch, Controller } from 'react-hook-form';

import { validateCustomerInfo } from 'src/state-machines/purchase/functions/validate-customer-info';
import { ProductPurchaseServiceContext } from 'src/contexts/state-machines/purchase/ProductPurchaseServiceContext';
import { RSFormMessageContainer } from 'src/reservation-page/forms/RSFormMessageContainer';

import { SectionFieldset } from 'src/organisms/product-purchase/SectionFieldset';
import { CustomerInfoInputTextField } from 'src/organisms/product-purchase/CustomerInfoInputTextField';
import { CustomerInfoInputTextArea } from 'src/organisms/product-purchase/CustomerInfoInputTextArea';
import { CustomerInfoInputSelect } from 'src/organisms/product-purchase/CustomerInfoInputSelect';
import { CustomerInfoInputRadio } from 'src/organisms/product-purchase/CustomerInfoInputRadio';
import { CustomerInfoInputCheckBox } from 'src/organisms/product-purchase/CustomerInfoInputCheckBox';

import { FormValues } from '../..';
import { captureException } from '@sentry/nextjs';

export const CustomerInfoFieldset: FC = () => {
  const intl = useIntl();

  const [, send] = ProductPurchaseServiceContext.useActor();

  const dataCustomerFields = ProductPurchaseServiceContext.useSelector(
    (state) => state.context.data.productDetail!.customer_fields,
  );

  const { control } = useFormContext<FormValues>();

  const watchedCustomerInfo = useWatch({
    control,
    name: 'customerInfo',
  });

  useEffect(() => {
    if (typeof watchedCustomerInfo === 'undefined') return;

    const customerInfo = Object.entries(watchedCustomerInfo)
      .filter(([id, value]) => {
        if (typeof value === 'undefined') {
          return false;
        }

        if (value.length === 0) {
          return false;
        }

        const parsedId = parseInt(id.replace('c_', ''), 10);

        return !isNaN(parsedId);
      })
      .map(([id, value]) => {
        return { id: parseInt(id.replace('c_', ''), 10), value };
      });

    send({
      type: 'INPUT_CUSTOMER_INFO',
      payload: { customerInfo },
    });
  }, [send, watchedCustomerInfo]);

  return (
    <SectionFieldset
      legend={intl.formatMessage({
        defaultMessage: '購入者情報',
        id: 'qc4b8J',
        description:
          'src/templates/merchants/resources/book/components/product-purchase-modal/SubscriptionPurchaseModal/components/MainView/components/CustomerInfoFieldset/index.tsx',
      })}
    >
      <div className="space-y-3">
        {dataCustomerFields.map((field) => {
          return (
            <div
              className="box-border border-b border-l-0 border-r-0 border-t-0 border-solid border-gray-50 pb-3 last-of-type:border-b-0 last-of-type:pb-0"
              key={`subscriptionPurchaseModal_customerInfo_${field.id}`}
            >
              {(() => {
                switch (field.answer_type) {
                  case 'text_field':
                    return (
                      <Controller
                        control={control}
                        name={`customerInfo.c_${field.id}`}
                        render={({
                          field: { value, onChange, onBlur, ref },
                          formState: { errors },
                        }) => {
                          if (Array.isArray(value)) {
                            captureException('Never');
                            throw new Error('Never');
                          }

                          const error = errors.customerInfo?.[`c_${field.id}`]
                            ?.message as string | undefined;

                          return (
                            <RSFormMessageContainer
                              errors={
                                typeof error !== 'undefined'
                                  ? [error]
                                  : undefined
                              }
                              isBlock
                            >
                              <CustomerInfoInputTextField
                                ref={ref}
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                                item={field}
                                isError={typeof error !== 'undefined'}
                              />
                            </RSFormMessageContainer>
                          );
                        }}
                        rules={{
                          validate: (value) => {
                            const result = validateCustomerInfo(field, value);
                            if (result.isValid) {
                              return undefined;
                            }

                            switch (result.type) {
                              case 'required':
                                return intl.formatMessage(
                                  {
                                    defaultMessage: '{label}を入力してください',
                                    id: 'k6YDs+',
                                    description:
                                      'src/templates/merchants/resources/book/components/product-purchase-modal/SubscriptionPurchaseModal/components/MainView/components/CustomerInfoFieldset/index.tsx',
                                  },
                                  {
                                    label: field.label,
                                  },
                                );
                              case 'format':
                                return intl.formatMessage(
                                  {
                                    defaultMessage:
                                      '{label}を正しく入力してください',
                                    id: 'CzEnK9',
                                    description:
                                      'src/templates/merchants/resources/book/components/product-purchase-modal/SubscriptionPurchaseModal/components/MainView/components/CustomerInfoFieldset/index.tsx',
                                  },
                                  {
                                    label: field.label,
                                  },
                                );
                            }
                          },
                        }}
                      />
                    );
                  case 'text_area':
                    return (
                      <Controller
                        control={control}
                        name={`customerInfo.c_${field.id}`}
                        render={({
                          field: { value, onChange, onBlur, ref },
                          formState: { errors },
                        }) => {
                          if (Array.isArray(value)) {
                            captureException('Never');
                            throw new Error('Never');
                          }

                          const error = errors.customerInfo?.[`c_${field.id}`]
                            ?.message as string | undefined;

                          return (
                            <RSFormMessageContainer
                              errors={
                                typeof error !== 'undefined'
                                  ? [error]
                                  : undefined
                              }
                              isBlock
                            >
                              <CustomerInfoInputTextArea
                                ref={ref}
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                                item={field}
                                isError={typeof error !== 'undefined'}
                              />
                            </RSFormMessageContainer>
                          );
                        }}
                        rules={{
                          validate: (value) => {
                            const result = validateCustomerInfo(field, value);
                            if (result.isValid) {
                              return undefined;
                            }

                            switch (result.type) {
                              case 'required':
                                return intl.formatMessage(
                                  {
                                    defaultMessage: '{label}を入力してください',
                                    id: 'k6YDs+',
                                    description:
                                      'src/templates/merchants/resources/book/components/product-purchase-modal/SubscriptionPurchaseModal/components/MainView/components/CustomerInfoFieldset/index.tsx',
                                  },
                                  {
                                    label: field.label,
                                  },
                                );
                              case 'format':
                                return intl.formatMessage(
                                  {
                                    defaultMessage:
                                      '{label}を正しく入力してください',
                                    id: 'CzEnK9',
                                    description:
                                      'src/templates/merchants/resources/book/components/product-purchase-modal/SubscriptionPurchaseModal/components/MainView/components/CustomerInfoFieldset/index.tsx',
                                  },
                                  {
                                    label: field.label,
                                  },
                                );
                            }
                          },
                        }}
                      />
                    );
                  case 'select_box':
                    return (
                      <Controller
                        control={control}
                        name={`customerInfo.c_${field.id}`}
                        render={({
                          field: { value, onChange, onBlur, ref },
                          formState: { errors },
                        }) => {
                          if (Array.isArray(value)) {
                            captureException('Never');
                            throw new Error('Never');
                          }

                          const error = errors.customerInfo?.[`c_${field.id}`]
                            ?.message as string | undefined;

                          return (
                            <RSFormMessageContainer
                              errors={
                                typeof error !== 'undefined'
                                  ? [error]
                                  : undefined
                              }
                              isBlock
                            >
                              <CustomerInfoInputSelect
                                ref={ref}
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                                item={field}
                                isError={typeof error !== 'undefined'}
                              />
                            </RSFormMessageContainer>
                          );
                        }}
                        rules={{
                          validate: (value) => {
                            const result = validateCustomerInfo(field, value);
                            if (result.isValid) {
                              return undefined;
                            }

                            switch (result.type) {
                              case 'required':
                                return intl.formatMessage(
                                  {
                                    defaultMessage: '{label}を入力してください',
                                    id: 'k6YDs+',
                                    description:
                                      'src/templates/merchants/resources/book/components/product-purchase-modal/SubscriptionPurchaseModal/components/MainView/components/CustomerInfoFieldset/index.tsx',
                                  },
                                  {
                                    label: field.label,
                                  },
                                );
                              case 'format':
                                return intl.formatMessage(
                                  {
                                    defaultMessage:
                                      '{label}を正しく入力してください',
                                    id: 'CzEnK9',
                                    description:
                                      'src/templates/merchants/resources/book/components/product-purchase-modal/SubscriptionPurchaseModal/components/MainView/components/CustomerInfoFieldset/index.tsx',
                                  },
                                  {
                                    label: field.label,
                                  },
                                );
                            }
                          },
                        }}
                      />
                    );
                  case 'radio_button':
                    return (
                      <Controller
                        control={control}
                        name={`customerInfo.c_${field.id}`}
                        render={({
                          field: { value, onChange, onBlur, ref },
                        }) => {
                          if (Array.isArray(value)) {
                            captureException('Never');
                            throw new Error('Never');
                          }

                          return (
                            <CustomerInfoInputRadio
                              ref={ref}
                              value={value}
                              onChange={onChange}
                              onBlur={onBlur}
                              item={field}
                            />
                          );
                        }}
                        rules={{
                          validate: (value) => {
                            const result = validateCustomerInfo(field, value);
                            if (result.isValid) {
                              return undefined;
                            }

                            switch (result.type) {
                              case 'required':
                                return intl.formatMessage(
                                  {
                                    defaultMessage: '{label}を入力してください',
                                    id: 'k6YDs+',
                                    description:
                                      'src/templates/merchants/resources/book/components/product-purchase-modal/SubscriptionPurchaseModal/components/MainView/components/CustomerInfoFieldset/index.tsx',
                                  },
                                  {
                                    label: field.label,
                                  },
                                );
                              case 'format':
                                return intl.formatMessage(
                                  {
                                    defaultMessage:
                                      '{label}を正しく入力してください',
                                    id: 'CzEnK9',
                                    description:
                                      'src/templates/merchants/resources/book/components/product-purchase-modal/SubscriptionPurchaseModal/components/MainView/components/CustomerInfoFieldset/index.tsx',
                                  },
                                  {
                                    label: field.label,
                                  },
                                );
                            }
                          },
                        }}
                      />
                    );
                  case 'checkbox':
                    return (
                      <Controller
                        control={control}
                        name={`customerInfo.c_${field.id}`}
                        render={({
                          field: { value, onChange, onBlur, ref },
                        }) => {
                          if (
                            typeof value !== 'undefined' &&
                            typeof value === 'string'
                          ) {
                            captureException('Never');
                            throw new Error('Never');
                          }

                          return (
                            <CustomerInfoInputCheckBox
                              ref={ref}
                              value={value}
                              onChange={onChange}
                              onBlur={onBlur}
                              item={field}
                            />
                          );
                        }}
                        rules={{
                          validate: (value) => {
                            const result = validateCustomerInfo(field, value);
                            if (result.isValid) {
                              return undefined;
                            }

                            switch (result.type) {
                              case 'required':
                                return intl.formatMessage(
                                  {
                                    defaultMessage: '{label}を入力してください',
                                    id: 'k6YDs+',
                                    description:
                                      'src/templates/merchants/resources/book/components/product-purchase-modal/SubscriptionPurchaseModal/components/MainView/components/CustomerInfoFieldset/index.tsx',
                                  },
                                  {
                                    label: field.label,
                                  },
                                );
                              case 'format':
                                return intl.formatMessage(
                                  {
                                    defaultMessage:
                                      '{label}を正しく入力してください',
                                    id: 'CzEnK9',
                                    description:
                                      'src/templates/merchants/resources/book/components/product-purchase-modal/SubscriptionPurchaseModal/components/MainView/components/CustomerInfoFieldset/index.tsx',
                                  },
                                  {
                                    label: field.label,
                                  },
                                );
                            }
                          },
                        }}
                      />
                    );
                  default:
                    return null;
                }
              })()}
            </div>
          );
        })}
      </div>
    </SectionFieldset>
  );
};
